import React from "react";
import { RequireAuth } from "./admin/route/RequireAuth";

import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { isGameHost } from "./config/gameHost";
import UserLayout from "./pages/route/UserLayout";
import UserThemeProvider from "./dark-theme/UserThemeProvider";
import PageViewTracker from "./pages/components/PageViewTracker";
import SeoHead from "./pages/components/SeoHead";
import AppWebViewLegalShell from "./pages/components/AppWebViewLegalShell";
import PiConsentRouteShell from "./pages/components/PiConsentRouteShell";
import ExternalRedirect from "./pages/components/ExternalRedirect";
import { SLIDING_PUZZLE_QUEST_APP_STORE_URL } from "./config/nativeAppsVisibility";
import { APP_SITE_ID } from "./config/appSiteLinks";
import { PROTOTYPE_MOBILE_PREFIX } from "./prototype/mobileRoutePrefix";
import TabletIntroPage from "./prototype/tablet-intro/TabletIntroPage";
import { TABLET_INTRO_PATH } from "./prototype/tablet-intro/routes";

// User
import Landing from "./pages/login/Landing";
import LandingAppFirstPage from "./pages/landing/LandingAppFirstPage";
import MobileAppsPage from "./pages/landing/MobileAppsPage";

// User - Policy
import SupportMain from "./pages/footer/SupportMain";
import MarketingMain from "./pages/footer/MarketingMain";
import AboutUsMain from "./pages/footer/AboutUsMain";
import TutorsLandingPage from "./pages/tutors/TutorsLandingPage";
import TutorsWorkspacePage from "./pages/tutors/TutorsWorkspacePage";
import TutorsPlayPage from "./pages/tutors/TutorsPlayPage";
import RequireTutor from "./pages/tutors/RequireTutor";
import {
    AppSiteLegalRedirect,
    AppSiteMarketingRedirect,
    AppSitePrivacyRedirect,
    AppSiteTermsRedirect,
} from "./pages/components/AppSitePageRedirect";
import HowToMain from "./pages/howto/HowToMain";
import LegalPage from "./pages/login/LegalPage";
import DeletionStatusPage from "./pages/login/data-deletion/DeletionStatusPage";
import DataDeletionRequestPage from "./pages/login/data-deletion/DataDeletionRequestPage";
import DataDeletionVerifiedPage from "./pages/login/data-deletion/DataDeletionVerifiedPage";

// User - Library
import CrossPlayMain from "./pages/puzzles/crossword/CrossPlayMain";
import GridPlayMain from "./pages/puzzles/grid/GridPlayMain";
import LibraryMain from "./pages/puzzles/library/LibraryMain";


import WordOfDayMain from "./pages/word-of-day/WordOfDayMain";
import CarouselStoryPage from "./pages/login/carousel/CarouselStoryPage";
import ADLayout from "./pages/advertisement/ADLayout";
import ADInsertPreviewPage from "./pages/advertisement/ADInsertPreviewPage";
import ADSlidingLogoPage from "./pages/advertisement/ADSlidingLogoPage";
import ADSlidingMasterFeatureGraphicPage from "./pages/advertisement/ADSlidingMasterFeatureGraphicPage";
import CompanyLogoPage from "./pages/advertisement/CompanyLogoPage";
import CompanyGoogleHeaderPage from "./pages/advertisement/CompanyGoogleHeaderPage";
import CompanyGoogleAdMobPage from "./pages/advertisement/CompanyGoogleAdMobPage";
import WebGoogleAdFramePage from "./pages/advertisement/WebGoogleAdFramePage";
import CompanyFacebookCoverPage from "./pages/advertisement/CompanyFacebookCoverPage";
import CompanyYoutubeCoverPage from "./pages/advertisement/CompanyYoutubeCoverPage";
import AppGoogleAdMobPage from "./pages/advertisement/AppGoogleAdMobPage";
import ADSafetyZonePage from "./pages/advertisement/ADSafetyZonePage";
import SlidingMasterLayout from "./prototype/mobile-sliding/layout/Layout";
import SlidingQuestLayout from "./prototype/android/sliding-puzzle-quest/layout/Layout";
import SlidingMasterLibraryPage from "./prototype/android/sliding-puzzle-quest/pages/LibraryPage";
import SlidingMasterMainPage from "./prototype/android/sliding-puzzle-quest/pages/MainPage";
import SlidingMasterMyPuzzlesPage from "./prototype/android/sliding-puzzle-quest/pages/MyPuzzlesPage";
import SlidingMasterPlayPage from "./prototype/android/sliding-puzzle-quest/pages/PlayPage";
import SlidingMasterSettingsPage from "./prototype/android/sliding-puzzle-quest/pages/SettingsPage";
import SlidingDraftGate from "./prototype/mobile-sliding/components/SlidingDraftGate";
import SlidingScreenshotIndexPage from "./prototype/mobile-sliding/pages/ScreenshotIndexPage";
import SlidingScreenshotScenePage from "./prototype/mobile-sliding/pages/ScreenshotScenePage";
import SlidingFeatureGraphicPage from "./prototype/mobile-sliding/pages/FeatureGraphicPage";
import SlidingFeatureGraphicEndingPage from "./prototype/mobile-sliding/pages/FeatureGraphicEndingPage";
import SlidingFeatureGraphicRecording1080pPage from "./prototype/mobile-sliding/pages/FeatureGraphicRecording1080pPage";
import SlidingFeatureGraphicInstagramPage from "./prototype/mobile-sliding/pages/FeatureGraphicInstagramPage";
import SlidingAppIconPage from "./prototype/mobile-sliding/pages/AppIconPage";
import SlidingAppIcon2Page from "./prototype/mobile-sliding/pages/AppIcon2Page";
import SlidingAppIcon3Page from "./prototype/mobile-sliding/pages/AppIcon3Page";
import SlidingAppIconFacebookPage from "./prototype/mobile-sliding/pages/AppIconFacebookPage";
import SlidingAnimationScenePage from "./prototype/mobile-sliding/animation/pages/AnimationScenePage";
import SlidingAnimationVideoPage from "./prototype/mobile-sliding/animation/pages/AnimationVideoPage";
import SlidingAnimationBrandPage from "./prototype/mobile-sliding/animation/pages/AnimationBrandPage";
import {
    ANIMATION_MAIN_PATH as SLIDING_ANIMATION_MAIN_PATH,
    ANIMATION_SCENE_PATH as SLIDING_ANIMATION_SCENE_PATH,
    ANIMATION_VIDEO_PATH as SLIDING_ANIMATION_VIDEO_PATH,
    ANIMATION_BRAND_PATH as SLIDING_ANIMATION_BRAND_PATH,
    animationPath as slidingAnimationPath,
} from "./prototype/mobile-sliding/animation/routes";
import {
    FEATURE_GRAPHIC_PATH as SLIDING_FEATURE_GRAPHIC_PATH,
    FEATURE_GRAPHIC_INSTAGRAM_PATH as SLIDING_FEATURE_GRAPHIC_INSTAGRAM_PATH,
    RECORDING_ENDING_PATH as SLIDING_RECORDING_ENDING_PATH,
    RECORDING_FEATURE_GRAPHIC_PATH as SLIDING_RECORDING_FEATURE_GRAPHIC_PATH,
    APP_ICON_PATH as SLIDING_APP_ICON_PATH,
    APP_ICON_2_PATH as SLIDING_APP_ICON_2_PATH,
    APP_ICON_3_PATH as SLIDING_APP_ICON_3_PATH,
    APP_ICON_FACEBOOK_PATH as SLIDING_APP_ICON_FACEBOOK_PATH,
    SCREENSHOT_SCENE_PATH as SLIDING_SCREENSHOT_SCENE_PATH,
    SCREENSHOTS_MAIN_PATH as SLIDING_SCREENSHOTS_MAIN_PATH,
} from "./prototype/mobile-sliding/routes";
import {
    LIBRARY_PATH as SLIDING_MASTER_LIBRARY_PATH,
    MAIN_PATH as SLIDING_MASTER_MAIN_PATH,
    MY_PUZZLES_PATH as SLIDING_MASTER_MY_PUZZLES_PATH,
    PLAY_PATH as SLIDING_MASTER_PLAY_PATH,
    SETTINGS_PATH as SLIDING_MASTER_SETTINGS_PATH,
} from "./prototype/android/sliding-puzzle-quest/routes";
import JigsawMobileLayout from "./prototype/mobile-jigsaw/layout/Layout";
import JigsawMobileMainPage from "./prototype/mobile-jigsaw/pages/MainPage";
import JigsawMobileScreenshotPlayPage from "./prototype/mobile-jigsaw/pages/ScreenshotPlayPage";
import JigsawMobileRecordingContentPage from "./prototype/mobile-jigsaw/pages/RecordingContentPage";
import JigsawMobileRecording1080pBoardPage from "./prototype/mobile-jigsaw/pages/Recording1080pBoardPage";
import JigsawMobileRecording1080pColorBasketPage from "./prototype/mobile-jigsaw/pages/Recording1080pColorBasketPage";
import JigsawMobileRecording1080pColorBasketFeaturePage from "./prototype/mobile-jigsaw/pages/Recording1080pColorBasketFeaturePage";
import JigsawAppIconPage from "./prototype/mobile-jigsaw/pages/AppIconPage";
import JigsawFeatureGraphicPage from "./prototype/mobile-jigsaw/pages/FeatureGraphicPage";
import JigsawFeatureGraphicEndingPage from "./prototype/mobile-jigsaw/pages/FeatureGraphicEndingPage";
import {
    MAIN_PATH as JIGSAW_MOBILE_MAIN_PATH,
    SCREENSHOT_PATH as JIGSAW_MOBILE_SCREENSHOT_PATH,
    RECORDING_APPLE_CONTENT_PATH as JIGSAW_RECORDING_APPLE_CONTENT_PATH,
    RECORDING_APPLE_PLAY_PATH as JIGSAW_RECORDING_APPLE_PLAY_PATH,
    RECORDING_COLOR_BASKET_FEATURE_PATH as JIGSAW_RECORDING_COLOR_BASKET_FEATURE_PATH,
    RECORDING_COLOR_BASKET_PATH as JIGSAW_RECORDING_COLOR_BASKET_PATH,
    RECORDING_CONTENT_PATH as JIGSAW_RECORDING_CONTENT_PATH,
    RECORDING_ENDING_PATH as JIGSAW_RECORDING_ENDING_PATH,
    RECORDING_PLAY_PATH as JIGSAW_RECORDING_PLAY_PATH,
    PLAY_BOARD_PATH as JIGSAW_PLAY_BOARD_PATH,
    APP_ICON_PATH as JIGSAW_APP_ICON_PATH,
    FEATURE_GRAPHIC_PATH as JIGSAW_FEATURE_GRAPHIC_PATH,
} from "./prototype/mobile-jigsaw/routes";
import WordWheelLayout from "./prototype/mobile-word-wheel/layout/Layout";
import WordWheelScreenshotLayout from "./prototype/mobile-word-wheel/layout/ScreenshotLayout";
import WordWheelDailyPage from "./prototype/mobile-word-wheel/pages/DailyPage";
import WordWheelMainPage from "./prototype/mobile-word-wheel/pages/MainPage";
import PrototypeWordWheelPlayPage from "./prototype/mobile-word-wheel/pages/PlayPage";
import WordWheelScreenshotScenePage from "./prototype/mobile-word-wheel/pages/ScreenshotScenePage";
import WordWheelScreenshotIndexPage from "./prototype/mobile-word-wheel/pages/ScreenshotIndexPage";
import WordWheelAppIconPage from "./prototype/mobile-word-wheel/pages/AppIconPage";
import WordWheelAppIcon2Page from "./prototype/mobile-word-wheel/pages/AppIcon2Page";
import WordWheelAppIcon3Page from "./prototype/mobile-word-wheel/pages/AppIcon3Page";
import WordWheelFeatureGraphicPage from "./prototype/mobile-word-wheel/pages/FeatureGraphicPage";
import WordWheelFeatureGraphicEndingPage from "./prototype/mobile-word-wheel/pages/FeatureGraphicEndingPage";
import WordWheelRecording1080pPlayPage from "./prototype/mobile-word-wheel/pages/Recording1080pPlayPage";
import WordWheelRecording1080pPacksPage from "./prototype/mobile-word-wheel/pages/Recording1080pPacksPage";
import WordWheelSettingsPage from "./prototype/mobile-word-wheel/pages/SettingsPage";
import {
    MAIN_PATH as WORD_WHEEL_MAIN_PATH,
    PLAY_PATH as WORD_WHEEL_PLAY_PATH,
    DAILY_PATH as WORD_WHEEL_DAILY_PATH,
    DAILY_PLAY_PATH as WORD_WHEEL_DAILY_PLAY_PATH,
    SETTINGS_PATH as WORD_WHEEL_SETTINGS_PATH,
    SCREENSHOTS_MAIN_PATH as WORD_WHEEL_SCREENSHOTS_MAIN_PATH,
    SCREENSHOT_SCENE_PATH as WORD_WHEEL_SCREENSHOT_SCENE_PATH,
    APP_ICON_PATH as WORD_WHEEL_APP_ICON_PATH,
    APP_ICON_2_PATH as WORD_WHEEL_APP_ICON_2_PATH,
    APP_ICON_3_PATH as WORD_WHEEL_APP_ICON_3_PATH,
    FEATURE_GRAPHIC_PATH as WORD_WHEEL_FEATURE_GRAPHIC_PATH,
    RECORDING_1080P_PATH as WORD_WHEEL_RECORDING_1080P_PATH,
    RECORDING_1080P_PACKS_PATH as WORD_WHEEL_RECORDING_1080P_PACKS_PATH,
    RECORDING_ENDING_PATH as WORD_WHEEL_RECORDING_ENDING_PATH,
} from "./prototype/mobile-word-wheel/routes";
import PuzzleOfDayPage from "./pages/puzzle-of-day/PuzzleOfDayPage";
import HomeBrowsePuzzlesPage from "./pages/browse-puzzles/HomeBrowsePuzzlesPage";
import HomeSlidingBrowsePage from "./pages/browse-sliding/HomeSlidingBrowsePage";
import SlidingSelectLevelPage from "./pages/puzzles/sliding/SlidingSelectLevelPage";
import SlidingPuzzlePlayPage from "./pages/puzzles/sliding/SlidingPuzzlePlayPage";
import HomeJigsawBrowsePage from "./pages/browse-jigsaw/HomeJigsawBrowsePage";
import JigsawPremiumPacks from "./pages/browse-jigsaw/JigsawPremiumPacks";
import HomeStorybookPage from "./pages/browse-stories/HomeStorybookPage";
import StoriesIndexPage from "./pages/browse-stories/StoriesIndexPage";
import StoriesToReadRedirect from "./pages/browse-stories/StoriesToReadRedirect";
import JigsawPlayPage from "./pages/puzzles/jigsaw/JigsawPlayPage";
import JigsawPlayBoardPage from "./pages/puzzles/jigsaw/JigsawPlayBoardPage";
import WordWheelHubPage from "./pages/puzzles/wordwheel/WordWheelHubPage";
import WordWheelPlayPage from "./pages/puzzles/wordwheel/WordWheelPlayPage";
import StoryCrossPlayPage from "./pages/story/StoryCrossPlayPage";
import StoryHiddenMessagePlay from "./pages/story/StoryHiddenMessagePlay";
import StoryWordSearchPlay from "./pages/story/StoryWordSearchPlay";

// User - Profile
import Login from "./pages/login/Login";
import Unauthorized from "./pages/login/Unauthorized";
import UserContactComplete from "./pages/contacts/UserContactComplete";
import UserContactMain from "./pages/profile/contact/UserContactMain";
import VocabularyMain from "./pages/profile/vocabulary/VocabularyMain";
import SettingsMain from "./pages/profile/settings/SettingsMain";
import UserProfileMain from "./pages/profile/UserProfileMain";
import UserDashboardPage from "./pages/dashboard/UserDashboardPage";
import { RequireUserAuth } from "./pages/route/RequireUserAuth";

// User - Referral
import ReferralEventPage from "./pages/referral/ReferralEventPage";
import ReferralInviteeAccept from "./pages/referral/ReferralInviteeAccept";

// User - System
import EmailVerified from "./pages/system/EmailVerified";
import { EmailSubscribe, EmailUnsubscribe } from "./pages/system/EmailMailingPreference";
import Error from "./pages/system/Error";
import NotFound from "./pages/system/NotFound";
// User - Subscription
import SubscriptionSuccess from "./pages/subscription/SubscriptionSuccess";
import SubscriptionPage from "./pages/subscription/SubscriptionPage";

// Admin
import Layout from "./admin/route/Layout";
import AdminStatisticsMain from "./admin/welcome/AdminStatisticsMain";
import WelcomeMain from "./admin/welcome/WelcomeMain";
import AppAndScreenshotLinksPage from "./admin/welcome/AppAndScreenshotLinksPage";
import ScreenshotGalleryAllPage from "./admin/welcome/ScreenshotGalleryAllPage";
import AppGoogleAdPage from "./admin/welcome/AppGoogleAdPage";
import AppFacebookAdPage from "./admin/welcome/AppFacebookAdPage";
import AppAmazonTabletPage from "./admin/welcome/AppAmazonTabletPage";

// Admin - Account
import AdminAccountMain from "./admin/account/AdminAccountMain";
import CreditMain from "./admin/account/credit/CreditMain";
import PuzzleCoinsMain from "./admin/account/coins/PuzzleCoinsMain";
import LeaderBoardMain from "./admin/account/leaderboard/LeaderBoardMain";
import SpeedRankMain from "./admin/account/speedrank/SpeedRankMain";
import UserDetails from "./admin/account/UserDetails";

// Admin - Contatct
import ContactMain from "./pages/contacts/ContactMain";

// Admin - Referral
import ReferralEventDetail from "./admin/referral/ReferralEventDetail";
import ReferralInvitersPage from "./admin/referral/ReferralInvitersPage";
import ReferralMain from "./admin/referral/ReferralMain";

// Admin - MailingListMain
import MailingListMain from "./admin/mailing_list/MailingListMain";
import {
    ADMIN_CRM_CONTACTS_PATH,
    ADMIN_CRM_FEEDBACK_PATH,
    ADMIN_CRM_MAILING_LIST_PATH,
    ADMIN_CRM_PATH,
    ADMIN_CRM_REFERRAL_PATH,
    ADMIN_CRM_REVIEW_PATH,
} from "./admin/crm/crmRoutes";
import {
    RedirectLegacyContacts,
    RedirectLegacyFeedback,
    RedirectLegacyFeedbackDetail,
    RedirectLegacyMailingList,
    RedirectLegacyReferral,
    RedirectLegacyReferralEvent,
    RedirectLegacyReferralInviters,
    RedirectToCrmHome,
} from "./admin/crm/CrmRedirects";

// Admin - Puzzle
import AdminPuzzleMain from "./admin/pzl_main/AdminPuzzleMain";


// Admin - Puzzle - Grid Template
import GridCreateTemplate from "./admin/pzl_grid/tpl/GridCreateTemplate";
import AdminGridTplDetail from "./admin/pzl_grid/tpl/AdminGridTplDetail";
import AdminGridTplMain from "./admin/pzl_grid/tpl/AdminGridTplMain";
import AdminGridPackMain from "./admin/pzl_grid/pack/AdminGridPackMain";
import AdminGridTplVerifyCoord from "./admin/pzl_grid/tpl/AdminGridTplVerifyCoord";
// Admin - Puzzle - Grid Image Upload (moved to system)
import AdminGridDetail from "./admin/system/pzl_hw_image_upload/AdminGridDetail";
import AdminGridMain from "./admin/system/pzl_hw_image_upload/AdminGridMain";
// Admin - Puzzle - Grid Solver
import AdminPlayMain from "./admin/pzl_grid/play/AdminPlayMain";

// Admin - Puzzle - Grid Board
import AdminGridBoardMain from "./admin/pzl_grid/board/AdminGridBoardMain";
import GridCandEdit from "./admin/pzl_grid/board/candidates/GridCandEdit";
import Step1Draft from "./admin/pzl_grid/board/Step1Draft";
import Step2ReadyToReview from "./admin/pzl_grid/board/Step2ReadyToReview";
import Step3Reviewed from "./admin/pzl_grid/board/Step3Reviewed";
import Step4Published from "./admin/pzl_grid/board/Step4Published";

// Admin - Puzzle - Crossword Board
import AdminCrossBoardMain from "./admin/pzl_cross/board/AdminCrossBoardMain";
import CrossWordCandEdit from "./admin/pzl_cross/board/candidates/CrossCandEdit";
import CrossStep1Draft from "./admin/pzl_cross/board/Step1Draft";
import CrossStep2ReadyToReview from "./admin/pzl_cross/board/Step2ReadyToReview";
import CrossStep3Reviewed from "./admin/pzl_cross/board/Step3Reviewed";
import CrossStep4Published from "./admin/pzl_cross/board/Step4Published";

// Admin - Puzzle - Crossword Template
import AdminCrossTplDetail from "./admin/pzl_cross/tpl/AdminCrossTplDetail";
import AdminCrossTplVerifyCoord from "./admin/pzl_cross/tpl/AdminCrossTplVerifyCoord";
import CrossCreateTemplate from "./admin/pzl_cross/tpl/CrossCreateTemplate";
import AdminCrossTplMain from "./admin/pzl_cross/tpl/AdminCrossTplMain";

// Admin - Puzzle - Crossword Play
import AdminCrossPlayMain from "./admin/pzl_cross/play/AdminCrossPlayMain";

// Admin - Puzzle - Word Wheel
import AdminWordWheelBoardMain from "./admin/pzl_wordwheel/board/AdminWordWheelBoardMain";
import WordWheelStep1Draft from "./admin/pzl_wordwheel/board/Step1Draft";
import WordWheelStep2ReadyToReview from "./admin/pzl_wordwheel/board/Step2ReadyToReview";
import WordWheelStep3Reviewed from "./admin/pzl_wordwheel/board/Step3Reviewed";
import WordWheelStep4Published from "./admin/pzl_wordwheel/board/Step4Published";
import WordWheelQuickPublisher from "./admin/pzl_wordwheel/board/WordWheelQuickPublisher";
import AdminWordWheelTplMain from "./admin/pzl_wordwheel/tpl/AdminWordWheelTplMain";
import AdminWordWheelPackMain from "./admin/pzl_wordwheel/pack/AdminWordWheelPackMain";
import AdminWordWheelTplDetail from "./admin/pzl_wordwheel/tpl/AdminWordWheelTplDetail";
import AdminWordWheelPlayMain from "./admin/pzl_wordwheel/play/AdminWordWheelPlayMain";
import AdminWordWheelStatsMain from "./admin/pzl_wordwheel/stats/AdminWordWheelStatsMain";
import AdminCrossStatsMain from "./admin/pzl_cross/stats/AdminCrossStatsMain";
import AdminGridStatsMain from "./admin/pzl_grid/stats/AdminGridStatsMain";


// Admin - SettingMain
import AdminFeedbackDetail from "./admin/feedback/AdminFeedbackDetail";
import AdminFeedbackMain from "./admin/feedback/AdminFeedbackMain";
import AdminAppReviewMain from "./admin/review/AdminAppReviewMain";
import AdminNotificationMain from "./admin/notification/AdminNotificationMain";
import AdminStoryDetail from "./admin/story/AdminStoryDetail";
import AdminSlidingMain from "./admin/story/AdminSlidingMain";
import AdminJigsawMain from "./admin/jigsaw/AdminJigsawMain";
import AdminJigsawDetailPage from "./admin/jigsaw/AdminJigsawDetailPage";
import AdminJigsawColorBasketDetailPage from "./admin/jigsaw/AdminJigsawColorBasketDetailPage";
import AdminJigsawTemplateMain from "./admin/jigsaw/AdminJigsawTemplateMain";
import AdminJigsawPackPage from "./admin/jigsaw/AdminJigsawPackPage";
import AdminJigsawPackDetailPage from "./admin/jigsaw/AdminJigsawPackDetailPage";
import CreateJigsawTemplatePage from "./admin/jigsaw/CreateJigsawTemplatePage";
import EditJigsawTemplatePage from "./admin/jigsaw/EditJigsawTemplatePage";
import AdminJigsawPlayMain from "./admin/jigsaw/play/AdminJigsawPlayMain";
import AdminJigsawPlayDetailPage from "./admin/jigsaw/play/AdminJigsawPlayDetailPage";
import AdminJigsawStatsMain from "./admin/jigsaw/stats/AdminJigsawStatsMain";
import AdminSlidingStoryDetail from "./admin/story/AdminSlidingStoryDetail";
import AdminSlidingPlayMain from "./admin/story/play/AdminSlidingPlayMain";
import AdminSlidingPlayDetailPage from "./admin/story/play/AdminSlidingPlayDetailPage";
import AdminSlidingStatsMain from "./admin/story/stats/AdminSlidingStatsMain";
import AdminSlidingPackPage from "./admin/story/pack/AdminSlidingPackPage";
import { ADMIN_SLIDING_LANDING_PATH } from "./admin/story/adminSlidingRoutes";
import AdminStoryMain from "./admin/story/AdminStoryMain";
import SettingMain from "./admin/setting/SettingMain";
import SysLogsMain from "./admin/setting/syslogs/SysLogsMain";

// Admin - Topic
import AdminTopicMain from "./admin/setting/topic/AdminTopicMain";
import CreateTopic from "./admin/setting/topic/CreateTopic";
import TopicDetail from "./admin/setting/topic/TopicDetail";

// Admin - Genre
import GenreMain from "./admin/setting/genre/GenreMain";

// Admin - Banned words
import BannedMain from "./admin/setting/banned_words/BannedMain";

// Admin - Bedrock Idea
import BedrockIdeaFormPage from "./admin/setting/bedrock_idea/BedrockIdeaFormPage";
import BedrockIdeaMain from "./admin/setting/bedrock_idea/BedrockIdeaMain";
// Admin - Publisher Schedule
import PublisherScheduleFormPage from "./admin/setting/publisher_schedule/PublisherScheduleFormPage";
import PublisherScheduleMain from "./admin/setting/publisher_schedule/PublisherScheduleMain";
// Admin - Task Scheduler
import TaskSchedulerFormPage from "./admin/setting/task_scheduler/TaskSchedulerFormPage";
import TaskSchedulerMain from "./admin/setting/task_scheduler/TaskSchedulerMain";

// Admin - Audio
import AdminAudioFormPage from "./admin/setting/audio/AdminAudioFormPage";
import AdminAudioMain from "./admin/setting/audio/AdminAudioMain";

// Admin - Advertisement
import AdResourceDetailPage from "./admin/advertisement/AdResourceDetailPage";
import AdvertisementMain from "./admin/advertisement/AdvertisementMain";
import GenerateAdImageMain from "./admin/advertisement/GenerateAdImageMain";
import IphoneBackgroundFromStoryMain, { PcBackgroundFromStoryMain } from "./admin/advertisement/IphoneBackgroundFromStoryMain";
import WordWheelGuessSocialMain from "./admin/advertisement/social/WordWheelGuessSocialMain";
import TutorsAdPage from "./admin/advertisement/TutorsAdPage";
import AdminTutorBoardPage from "./admin/tutor/AdminTutorBoardPage";
import ProductIconsPage from "./admin/advertisement/ProductIconsPage";
import PromotionMain from "./admin/advertisement/PromotionMain";
import PromotionEditPage from "./admin/advertisement/PromotionEditPage";
import WeeklyIssuePage from "./admin/advertisement/WeeklyIssuePage";

// Admin - System
import AnythingMain from "./admin/system/AnythingMain";
import DicPlayWordMain from "./admin/system/dic-play/DicPlayWordMain";
import WordOfDayBatchMain from "./admin/system/word-of-day-batch/WordOfDayBatchMain";
import CreateFrenchPuzzlesMain from "./admin/system/french/CreateFrenchPuzzlesMain";
import IngestMain from "./admin/system/ingest/IngestMain";
import PrototypeMain from "./admin/system/PrototypeMain";
import CambridgeMain from "./admin/system/integrate/CambridgeMain";
import DatamuseApiMain from "./admin/system/integrate/DatamuseApiMain";
import DictionaryApiMain from "./admin/system/integrate/DictionaryApiMain";
import GuardianMain from "./admin/system/integrate/GuardianMain";
import IntegrateMain from "./admin/system/integrate/IntegrateMain";
import OpenLibraryDetail from "./admin/system/integrate/OpenLibraryDetail";
import OpenLibraryMain from "./admin/system/integrate/OpenLibraryMain";
import PiDicApiMain from "./admin/system/integrate/PiDicApiMain";
import WordNetworkMain from "./admin/system/integrate/WordNetworkMain";
import SystemMain from "./admin/system/SystemMain";
import TagMigrationMain from "./admin/system/tag-migration/TagMigrationMain";

// Admin - Test
import CredentialsMain from "./admin/system/credentials/CredentialsMain";
import CryptoMain from "./admin/system/CryptoMain";
import EmailMain from "./admin/system/email/EmailMain";
import FileUploadMain from "./admin/system/file-upload/FileUploadMain";
import GenImagePreviewMain from "./admin/system/gen-image-preview/GenImagePreviewMain";
import StripeMain from "./admin/system/stripe/StripeMain";
import TranslateMain from "./admin/system/translate/TranslateMain";
import HookItMain from "./admin/system/hook-it/HookItMain";
import WordSearchPlaceWordsMain from "./admin/system/wordsearch-place-words/WordSearchPlaceWordsMain";
import TestInstagramMain from "./admin/system/instagram/TestInstagramMain";
import TestFacebookMain from "./admin/system/facebook/TestFacebookMain";
import BoilerplateDataGrid from "./admin/test/BoilerplateDataGrid";
import TestJigsawMain from "./admin/test/TestJigsawMain";
import TestJigsawCanvasPlayPage from "./admin/jigsaw-test/TestJigsawCanvasPlayPage";
import SvgToJsonPage from "./admin/system/SvgToJsonPage";
import SvgOverlayConverterPage from "./admin/system/SvgOverlayConverterPage";
import TestPromptV2Main from "./admin/system/test-prompt-v2/TestPromptV2Main";
import TestWordPuzzlePhotoMain from "./admin/system/test-word-puzzle-photo/TestWordPuzzlePhotoMain";
import AdSenseTestMain from "./admin/system/adsense/AdSenseTestMain";
import MonetagTestMain from "./admin/system/monetag/MonetagTestMain";
import SendNotificationMain from "./admin/system/send-notification/SendNotificationMain";

// Creator
import CreatorCrossEdit from "./creator/cross/CreatorCrossEdit";
import CreatorCrosswordMain from "./creator/cross/CreatorCrosswordMain";
import CreatorGridEdit from "./creator/grid/CreatorGridEdit";
import CreatorGridEditPage from "./creator/grid/CreatorGridEditPage";
import CreatorGridMain from "./creator/grid/CreatorGridMain";
import CreatorLayout from "./creator/CreatorLayout";
import CreatorMain from "./creator/CreatorMain";
import QuickStartPage from "./creator/guide/QuickStartPage";

/** Redirect legacy `/mobile/*` bookmarks to `/prototype/mobile/*`. */
function LegacyMobileRedirect() {
    const { pathname, search, hash } = useLocation();
    const target = pathname.replace(/^\/mobile(?=\/|$)/, PROTOTYPE_MOBILE_PREFIX) + search + hash;
    return <Navigate to={target} replace />;
}

const createAuthRoute = (path, element) => (
    <Route
        path={path}
        element={
            <RequireAuth>
                <Layout>{element}</Layout>
            </RequireAuth>
        }
    />
);


const RootRoute = () => {
    if (isGameHost()) {
        return <Navigate to="/admin" replace />;
    }
    return (
        <UserLayout>
            <LandingAppFirstPage />
        </UserLayout>
    );
};

/** Login / 404: no site header. */
function PublicChromeLessPage({ children }) {
    return (
        <UserThemeProvider>
            {children}
        </UserThemeProvider>
    );
}

/** Legacy `/story/play/:id` → `/story/play-cross/:id`. */
function RedirectStoryPlayToPlayCross() {
    const { id } = useParams();
    return <Navigate to={`/story/play-cross/${encodeURIComponent(id)}`} replace />;
}

/** `/puzzles/foo/` → `/puzzles/foo` (client-side; production 301s are in public/serve.json). */
function RedirectPuzzleListingTrailingSlash() {
    const { pathname, search, hash } = useLocation();
    if (pathname === "/" || !pathname.endsWith("/")) {
        return null;
    }
    return (
        <Navigate
            to={`${pathname.replace(/\/+$/, "")}${search}${hash}`}
            replace
        />
    );
}

/** Legacy `/puzzle/play-grid/:type/:id` → flat play URLs. */
function RedirectLegacyPuzzlePlayGrid() {
    const { type, id } = useParams();
    if (!id) return <Navigate to="/puzzles/word-search" replace />;
    const t = String(type || "").toLowerCase();
    if (t === "hidden_message") {
        return <Navigate to={`/puzzle/play-hidden-message/${encodeURIComponent(id)}`} replace />;
    }
    if (t === "word_search") {
        return <Navigate to={`/puzzle/play-word-search/${encodeURIComponent(id)}`} replace />;
    }
    return <Navigate to="/puzzles/crossword" replace />;
}

function App() {
    
    return (
        <>
            <SeoHead />
            <AppWebViewLegalShell />
            <PiConsentRouteShell />
            <PageViewTracker />
            <RedirectPuzzleListingTrailingSlash />
            <Routes>
            {/* **************  User ************** */}
            {/* Root: public landing */}
            <Route index element={<RootRoute />} />
            <Route
                path="/landing"
                element={
                    <UserLayout>
                        <Landing />
                    </UserLayout>
                }
            />
            {/* No site chrome on these two, but they still follow the site theme. */}
            <Route
                path="/login"
                element={
                    <PublicChromeLessPage>
                        <Login />
                    </PublicChromeLessPage>
                }
            />
            <Route
                path="/notfound"
                element={
                    <PublicChromeLessPage>
                        <NotFound />
                    </PublicChromeLessPage>
                }
            />

            {/* Creator Routes */}
            <Route path="/creator" element={
                <CreatorLayout>
                    <CreatorMain />
                </CreatorLayout>
            } />
            <Route path="/creator/guide" element={
                <CreatorLayout>
                    <QuickStartPage />
                </CreatorLayout>
            } />
            <Route path="/creator/grid" element={
                <CreatorLayout>
                    <CreatorGridMain />
                </CreatorLayout>
            } />
            <Route path="/creator/grid/edit" element={
                <CreatorLayout>
                    <CreatorGridEdit />
                </CreatorLayout>
            } />
            <Route path="/creator/grid/edit/:gridId" element={
                <CreatorLayout>
                    <CreatorGridEditPage />
                </CreatorLayout>
            } />
            <Route path="/creator/crossword" element={
                <CreatorLayout>
                    <CreatorCrosswordMain />
                </CreatorLayout>
            } />
            <Route path="/creator/crossword/edit" element={
                <CreatorLayout>
                    <CreatorCrossEdit />
                </CreatorLayout>
            } />
            <Route path="/creator/crossword/edit/:crossId" element={
                <CreatorLayout>
                    <CrossWordCandEdit />
                </CreatorLayout>
            } />

            <Route path="/dashboard" element={
                <RequireUserAuth>
                    <UserLayout>
                        <UserDashboardPage />
                    </UserLayout>
                </RequireUserAuth>
            } />

            {/* Account Routes - require login */}
            <Route path="/account/profile" element={
                <RequireUserAuth>
                    <UserLayout>
                        <UserProfileMain />
                    </UserLayout>
                </RequireUserAuth>
            } />
            <Route path="/account/settings" element={
                <RequireUserAuth>
                    <UserLayout>
                        <SettingsMain />
                    </UserLayout>
                </RequireUserAuth>
            } />
            <Route path="/account/vocabulary" element={
                <RequireUserAuth>
                    <UserLayout>
                        <VocabularyMain />
                    </UserLayout>
                </RequireUserAuth>
            } />

            {/* Referral Routes */}
            <Route path="/referral/:eventId" element={
                <UserLayout>
                    <ReferralEventPage />
                </UserLayout>
            } />
            <Route path="/referral/invitee_accept" element={
                <UserLayout>
                    <ReferralInviteeAccept />
                </UserLayout>
            } />

            {/* Legacy puzzle hub URLs → unified browse */}
            <Route
                path="/redirect/apple/sliding-puzzle-quest"
                element={<ExternalRedirect href={SLIDING_PUZZLE_QUEST_APP_STORE_URL} />}
            />
            <Route path="/puzzle/word_search" element={<Navigate to="/puzzles/word-search" replace />} />
            <Route path="/puzzle/hidden_message" element={<Navigate to="/puzzles/hidden-message" replace />} />
            <Route path="/puzzle/crossword" element={<Navigate to="/puzzles/crossword" replace />} />
            <Route path="/word-of-day/:ymd" element={
                <UserLayout>
                    <WordOfDayMain />
                </UserLayout>
            } />
            <Route path="/word-of-day" element={
                <UserLayout>
                    <WordOfDayMain />
                </UserLayout>
            } />
            <Route path="/read-story/:storyId" element={
                <UserLayout>
                    <CarouselStoryPage />
                </UserLayout>
            } />
            <Route
                path="/ad/insert"
                element={
                    <ADLayout>
                        <ADInsertPreviewPage />
                    </ADLayout>
                }
            />
            <Route
                path="/ad/safety-zone"
                element={
                    <ADLayout>
                        <ADSafetyZonePage />
                    </ADLayout>
                }
            />
            <Route
                path="/ad/sliding-logo"
                element={
                    <ADLayout>
                        <ADSlidingLogoPage />
                    </ADLayout>
                }
            />
            <Route path="/ad/company-logo" element={<CompanyLogoPage />} />
            <Route path="/ad/company-google-header" element={<CompanyGoogleHeaderPage />} />
            <Route path="/ad/company-google-admob" element={<CompanyGoogleAdMobPage />} />
            <Route path="/ad/web-google-ads" element={<WebGoogleAdFramePage />} />
            <Route path="/ad/company-facebook-cover" element={<CompanyFacebookCoverPage />} />
            <Route path="/ad/company-youtube-cover" element={<CompanyYoutubeCoverPage />} />
            <Route
                path="/ad/stripe-product-icons"
                element={<Navigate to="/admin/advertisement/product-icon" replace />}
            />
            <Route
                path="/ad/jigsaw-muse-google-admob"
                element={<AppGoogleAdMobPage appKey="jigsaw-muse" />}
            />
            <Route
                path="/ad/word-wheel-quest-google-admob"
                element={<AppGoogleAdMobPage appKey="word-wheel-quest" />}
            />
            <Route
                path="/ad/sliding-puzzle-quest-google-admob"
                element={<AppGoogleAdMobPage appKey="sliding-puzzle-quest" />}
            />
            <Route
                path="/ad/sliding-master-feature-graphic"
                element={
                    <ADLayout variant="light">
                        <ADSlidingMasterFeatureGraphicPage />
                    </ADLayout>
                }
            />
            <Route path="/mobile/*" element={<LegacyMobileRedirect />} />
            <Route
                path={SLIDING_MASTER_MAIN_PATH}
                element={
                    <SlidingQuestLayout>
                        <SlidingDraftGate>
                            <SlidingMasterMainPage />
                        </SlidingDraftGate>
                    </SlidingQuestLayout>
                }
            />
            <Route
                path={SLIDING_MASTER_PLAY_PATH}
                element={
                    <SlidingQuestLayout>
                        <SlidingDraftGate>
                            <SlidingMasterPlayPage />
                        </SlidingDraftGate>
                    </SlidingQuestLayout>
                }
            />
            <Route
                path={SLIDING_MASTER_LIBRARY_PATH}
                element={
                    <SlidingQuestLayout>
                        <SlidingDraftGate>
                            <SlidingMasterLibraryPage />
                        </SlidingDraftGate>
                    </SlidingQuestLayout>
                }
            />
            <Route
                path={SLIDING_MASTER_MY_PUZZLES_PATH}
                element={
                    <SlidingQuestLayout>
                        <SlidingDraftGate>
                            <SlidingMasterMyPuzzlesPage />
                        </SlidingDraftGate>
                    </SlidingQuestLayout>
                }
            />
            <Route
                path={SLIDING_MASTER_SETTINGS_PATH}
                element={
                    <SlidingQuestLayout>
                        <SlidingDraftGate>
                            <SlidingMasterSettingsPage />
                        </SlidingDraftGate>
                    </SlidingQuestLayout>
                }
            />
            <Route
                path={SLIDING_SCREENSHOTS_MAIN_PATH}
                element={
                    <SlidingMasterLayout>
                        <SlidingScreenshotIndexPage />
                    </SlidingMasterLayout>
                }
            />
            <Route path={TABLET_INTRO_PATH} element={<TabletIntroPage />} />
            <Route
                path={SLIDING_SCREENSHOT_SCENE_PATH}
                element={
                    <SlidingMasterLayout>
                        <SlidingScreenshotScenePage />
                    </SlidingMasterLayout>
                }
            />
            <Route
                path={SLIDING_FEATURE_GRAPHIC_PATH}
                element={<SlidingFeatureGraphicPage />}
            />
            <Route
                path={SLIDING_FEATURE_GRAPHIC_INSTAGRAM_PATH}
                element={<SlidingFeatureGraphicInstagramPage />}
            />
            <Route
                path={SLIDING_RECORDING_ENDING_PATH}
                element={<SlidingFeatureGraphicEndingPage />}
            />
            <Route
                path={SLIDING_RECORDING_FEATURE_GRAPHIC_PATH}
                element={<SlidingFeatureGraphicRecording1080pPage />}
            />
            <Route
                path={SLIDING_APP_ICON_PATH}
                element={<SlidingAppIconPage />}
            />
            <Route
                path={SLIDING_APP_ICON_2_PATH}
                element={<SlidingAppIcon2Page />}
            />
            <Route
                path={SLIDING_APP_ICON_3_PATH}
                element={<SlidingAppIcon3Page />}
            />
            <Route
                path={SLIDING_APP_ICON_FACEBOOK_PATH}
                element={<SlidingAppIconFacebookPage />}
            />
            <Route
                path={SLIDING_ANIMATION_SCENE_PATH}
                element={
                    <SlidingMasterLayout>
                        <SlidingAnimationScenePage />
                    </SlidingMasterLayout>
                }
            />
            <Route
                path={SLIDING_ANIMATION_VIDEO_PATH}
                element={
                    <SlidingMasterLayout>
                        <SlidingAnimationVideoPage />
                    </SlidingMasterLayout>
                }
            />
            <Route
                path={SLIDING_ANIMATION_BRAND_PATH}
                element={<SlidingAnimationBrandPage />}
            />
            <Route
                path={SLIDING_ANIMATION_MAIN_PATH}
                element={<Navigate to={slidingAnimationPath("01")} replace />}
            />
            <Route
                path={JIGSAW_MOBILE_MAIN_PATH}
                element={
                    <JigsawMobileLayout>
                        <JigsawMobileMainPage />
                    </JigsawMobileLayout>
                }
            />
            <Route
                path={JIGSAW_MOBILE_SCREENSHOT_PATH}
                element={
                    <JigsawMobileLayout>
                        <JigsawMobileScreenshotPlayPage />
                    </JigsawMobileLayout>
                }
            />
            <Route
                path={JIGSAW_RECORDING_CONTENT_PATH}
                element={
                    <JigsawMobileLayout>
                        <JigsawMobileRecordingContentPage />
                    </JigsawMobileLayout>
                }
            />
            <Route
                path={JIGSAW_RECORDING_COLOR_BASKET_PATH}
                element={
                    <JigsawMobileLayout>
                        <JigsawMobileRecording1080pColorBasketPage />
                    </JigsawMobileLayout>
                }
            />
            <Route
                path={JIGSAW_RECORDING_COLOR_BASKET_FEATURE_PATH}
                element={
                    <JigsawMobileLayout>
                        <JigsawMobileRecording1080pColorBasketFeaturePage />
                    </JigsawMobileLayout>
                }
            />
            <Route
                path={JIGSAW_RECORDING_PLAY_PATH}
                element={
                    <JigsawMobileLayout>
                        <JigsawMobileRecording1080pBoardPage />
                    </JigsawMobileLayout>
                }
            />
            <Route
                path={JIGSAW_RECORDING_APPLE_CONTENT_PATH}
                element={
                    <JigsawMobileLayout>
                        <JigsawMobileRecordingContentPage />
                    </JigsawMobileLayout>
                }
            />
            <Route
                path={JIGSAW_RECORDING_APPLE_PLAY_PATH}
                element={
                    <JigsawMobileLayout>
                        <JigsawMobileRecording1080pBoardPage />
                    </JigsawMobileLayout>
                }
            />
            <Route
                path={JIGSAW_PLAY_BOARD_PATH}
                element={
                    <JigsawMobileLayout>
                        <JigsawPlayBoardPage />
                    </JigsawMobileLayout>
                }
            />
            <Route path={JIGSAW_APP_ICON_PATH} element={<JigsawAppIconPage />} />
            <Route path={JIGSAW_FEATURE_GRAPHIC_PATH} element={<JigsawFeatureGraphicPage />} />
            <Route path={JIGSAW_RECORDING_ENDING_PATH} element={<JigsawFeatureGraphicEndingPage />} />
            <Route
                path={WORD_WHEEL_MAIN_PATH}
                element={
                    <WordWheelLayout>
                        <WordWheelMainPage />
                    </WordWheelLayout>
                }
            />
            <Route
                path={WORD_WHEEL_PLAY_PATH}
                element={
                    <WordWheelLayout>
                        <PrototypeWordWheelPlayPage />
                    </WordWheelLayout>
                }
            />
            <Route
                path={WORD_WHEEL_SCREENSHOTS_MAIN_PATH}
                element={
                    <WordWheelScreenshotLayout>
                        <WordWheelScreenshotIndexPage />
                    </WordWheelScreenshotLayout>
                }
            />
            <Route
                path={WORD_WHEEL_SCREENSHOT_SCENE_PATH}
                element={
                    <WordWheelScreenshotLayout>
                        <WordWheelScreenshotScenePage />
                    </WordWheelScreenshotLayout>
                }
            />
            <Route path={WORD_WHEEL_APP_ICON_PATH} element={<WordWheelAppIconPage />} />
            <Route path={WORD_WHEEL_APP_ICON_2_PATH} element={<WordWheelAppIcon2Page />} />
            <Route path={WORD_WHEEL_APP_ICON_3_PATH} element={<WordWheelAppIcon3Page />} />
            <Route path={WORD_WHEEL_FEATURE_GRAPHIC_PATH} element={<WordWheelFeatureGraphicPage />} />
            <Route path={WORD_WHEEL_RECORDING_1080P_PATH} element={<WordWheelRecording1080pPlayPage />} />
            <Route path={WORD_WHEEL_RECORDING_1080P_PACKS_PATH} element={<WordWheelRecording1080pPacksPage />} />
            <Route path={WORD_WHEEL_RECORDING_ENDING_PATH} element={<WordWheelFeatureGraphicEndingPage />} />
            <Route
                path={WORD_WHEEL_DAILY_PATH}
                element={
                    <RequireAuth>
                        <WordWheelLayout>
                            <WordWheelDailyPage />
                        </WordWheelLayout>
                    </RequireAuth>
                }
            />
            <Route
                path={WORD_WHEEL_DAILY_PLAY_PATH}
                element={
                    <RequireAuth>
                        <WordWheelLayout>
                            <PrototypeWordWheelPlayPage />
                        </WordWheelLayout>
                    </RequireAuth>
                }
            />
            <Route
                path={WORD_WHEEL_SETTINGS_PATH}
                element={
                    <WordWheelLayout>
                        <WordWheelSettingsPage />
                    </WordWheelLayout>
                }
            />
            <Route path="/puzzle-of-day/:storyId" element={
                <UserLayout>
                    <PuzzleOfDayPage />
                </UserLayout>
            } />
            <Route path="/puzzle-of-day" element={
                <UserLayout>
                    <PuzzleOfDayPage />
                </UserLayout>
            } />
            <Route path="/storybook" element={
                <UserLayout>
                    <HomeStorybookPage />
                </UserLayout>
            } />
            <Route path="/story-of-day" element={<Navigate to="/puzzle-of-day" replace />} />
            <Route path="/stories" element={
                <UserLayout>
                    <StoriesIndexPage />
                </UserLayout>
            } />
            <Route path="/stories/:storyId" element={<StoriesToReadRedirect />} />
            <Route path="/browse-puzzles" element={<Navigate to="/puzzles/crossword" replace />} />
            <Route path="/puzzles/crossword" element={
                <UserLayout>
                    <HomeBrowsePuzzlesPage browseKind="CROSSWORD" />
                </UserLayout>
            } />
            <Route path="/puzzles/word-search" element={
                <UserLayout>
                    <HomeBrowsePuzzlesPage browseKind="WORD_SEARCH" />
                </UserLayout>
            } />
            <Route path="/puzzles/hidden-message" element={
                <UserLayout>
                    <HomeBrowsePuzzlesPage browseKind="HIDDEN_MESSAGE" />
                </UserLayout>
            } />
            <Route path="/puzzles/sliding" element={
                <UserLayout>
                    <HomeSlidingBrowsePage />
                </UserLayout>
            } />
            {/* New Refactored Routes */}
            <Route path="/account/library" element={
                <UserLayout>
                    <LibraryMain />
                </UserLayout>
            } />
            <Route path="/library" element={<Navigate to="/account/library" replace />} />
            <Route path="/puzzle/play-hidden-message/:id" element={
                <UserLayout>
                    <GridPlayMain />
                </UserLayout>
            } />
            <Route path="/puzzle/play-word-search/:id" element={
                <UserLayout>
                    <GridPlayMain />
                </UserLayout>
            } />
            <Route path="/puzzle/play-grid/:type/:id" element={<RedirectLegacyPuzzlePlayGrid />} />
            <Route path="/puzzle/play-cross/:id" element={
                <UserLayout>
                    <CrossPlayMain />
                </UserLayout>
            } />
            <Route path="/puzzle/play-sliding/:storyId" element={
                <UserLayout>
                    <SlidingSelectLevelPage />
                </UserLayout>
            } />
            <Route path="/puzzle/play-sliding/:storyId/board/:slidingId" element={
                <UserLayout>
                    <SlidingPuzzlePlayPage />
                </UserLayout>
            } />
            <Route path="/puzzles/jigsaw/packs" element={
                <UserLayout>
                    <JigsawPremiumPacks />
                </UserLayout>
            } />
            <Route path="/puzzles/jigsaw" element={
                <UserLayout>
                    <HomeJigsawBrowsePage />
                </UserLayout>
            } />
            <Route path="/puzzles/word-wheel" element={
                <UserLayout>
                    <WordWheelHubPage />
                </UserLayout>
            } />
            <Route path="/puzzles/word-wheel/pack/:packCode/:level" element={
                <UserLayout>
                    <WordWheelPlayPage />
                </UserLayout>
            } />
            <Route path="/puzzles/word-wheel/pack/:packCode" element={
                <UserLayout>
                    <WordWheelPlayPage />
                </UserLayout>
            } />
            <Route path="/puzzles/word-wheel/journey/:level" element={
                <UserLayout>
                    <WordWheelPlayPage />
                </UserLayout>
            } />
            <Route path="/puzzles/word-wheel/journey" element={
                <UserLayout>
                    <WordWheelPlayPage />
                </UserLayout>
            } />
            <Route path="/puzzles/word-wheel/daily" element={
                <UserLayout>
                    <WordWheelPlayPage />
                </UserLayout>
            } />
            <Route path="/puzzle/play-word-wheel/:tplId" element={
                <UserLayout>
                    <WordWheelPlayPage />
                </UserLayout>
            } />
            <Route path="/puzzle/play-jigsaw/:storyId" element={
                <UserLayout>
                    <JigsawPlayPage />
                </UserLayout>
            } />
            <Route path="/puzzle/play-jigsaw/:storyId/board/:jigsawId" element={
                <UserLayout>
                    <JigsawPlayBoardPage />
                </UserLayout>
            } />
            <Route path="/puzzle/play-jigsaw/:storyId/board/:jigsawId/basket/:basketIndex" element={
                <UserLayout>
                    <JigsawPlayBoardPage />
                </UserLayout>
            } />
            <Route path="/story/play-cross/:id" element={
                <UserLayout>
                    <StoryCrossPlayPage />
                </UserLayout>
            } />
            <Route path="/story/play-grid/hidden_message/:id" element={
                <UserLayout>
                    <StoryHiddenMessagePlay />
                </UserLayout>
            } />
            <Route path="/story/play-grid/word_search/:id" element={
                <UserLayout>
                    <StoryWordSearchPlay />
                </UserLayout>
            } />
            <Route path="/story/play/:id" element={<RedirectStoryPlayToPlayCross />} />

            {/* Contact Routes */}
            <Route path="/contact" element={
                <UserLayout>
                    <UserContactMain />
                </UserLayout>
            } />
            <Route path="/contact/detail/:fullName" element={
                <UserLayout>
                    <UserContactComplete />
                </UserLayout>
            } />
            <Route path="/legal" element={<AppSiteLegalRedirect />} />
            <Route path="/legal/:appId" element={
                <UserLayout>
                    <LegalPage />
                </UserLayout>
            } />
            <Route path="/deletion-status/:code?" element={
                <UserLayout>
                    <DeletionStatusPage />
                </UserLayout>
            } />
            <Route path="/data-deletion/request" element={
                <UserLayout>
                    <DataDeletionRequestPage />
                </UserLayout>
            } />
            <Route path="/data-deletion/verified" element={
                <UserLayout>
                    <DataDeletionVerifiedPage />
                </UserLayout>
            } />
            <Route path="/privacy" element={<AppSitePrivacyRedirect />} />
            <Route path="/privacy_policy" element={<AppSitePrivacyRedirect />} />
            <Route path="/privacy_policy/:appId" element={
                <UserLayout>
                    <LegalPage />
                </UserLayout>
            } />
            <Route path="/terms" element={<AppSiteTermsRedirect />} />
            <Route path="/terms_of_service" element={<AppSiteTermsRedirect />} />
            <Route path="/terms_of_service/:appId" element={
                <UserLayout>
                    <LegalPage />
                </UserLayout>
            } />
            <Route path="/howto/*" element={
                <UserLayout>
                    <HowToMain />
                </UserLayout>
            } />
            <Route path="/subscription/plans" element={
                <UserLayout>
                    <SubscriptionPage />
                </UserLayout>
            } />
            <Route path="/support" element={<Navigate to={`/support/${APP_SITE_ID.WEB}`} replace />} />
            <Route path="/support/:appId" element={
                <UserLayout>
                    <SupportMain />
                </UserLayout>
            } />
            <Route path="/marketing" element={<AppSiteMarketingRedirect />} />
            <Route path="/marketing/:appId" element={
                <UserLayout>
                    <MarketingMain />
                </UserLayout>
            } />
            <Route path="/about" element={
                <UserLayout>
                    <AboutUsMain />
                </UserLayout>
            } />
            <Route path="/mobile-apps" element={
                <UserLayout>
                    <MobileAppsPage />
                </UserLayout>
            } />
            <Route path="/tutors" element={
                <UserLayout>
                    <TutorsLandingPage />
                </UserLayout>
            } />
            <Route path="/tutors/groups" element={<Navigate to="/tutors" replace />} />
            <Route path="/tutors/dashboard" element={<Navigate to="/tutors" replace />} />
            <Route path="/tutors/library/:id/play" element={
                <UserLayout>
                    <RequireTutor>
                        <TutorsPlayPage />
                    </RequireTutor>
                </UserLayout>
            } />
            <Route path="/tutors/library" element={
                <UserLayout>
                    <RequireTutor>
                        <TutorsWorkspacePage section="library" />
                    </RequireTutor>
                </UserLayout>
            } />
            <Route path="/tutors/worksheets" element={
                <UserLayout>
                    <RequireTutor>
                        <TutorsWorkspacePage section="worksheets" />
                    </RequireTutor>
                </UserLayout>
            } />
            <Route path="/tutors/settings" element={
                <UserLayout>
                    <RequireTutor>
                        <TutorsWorkspacePage section="settings" />
                    </RequireTutor>
                </UserLayout>
            } />
            <Route path="/for-educators" element={<Navigate to="/tutors" replace />} />
            <Route path="/faq" element={<Navigate to={`/support/${APP_SITE_ID.WEB}`} replace />} />
            <Route path="/email/verified" element={
                <UserLayout>
                    <EmailVerified />
                </UserLayout>
            } />
            <Route path="/email/unsubscribe" element={
                <UserLayout>
                    <EmailUnsubscribe />
                </UserLayout>
            } />
            <Route path="/email/subscribe" element={
                <UserLayout>
                    <EmailSubscribe />
                </UserLayout>
            } />
            <Route path="/verified" element={
                <UserLayout>
                    <EmailVerified />
                </UserLayout>
            } />
            <Route path="/error" element={
                <UserLayout>
                    <Error />
                </UserLayout>
            } />
            <Route path="/unauthorized" element={
                <UserLayout>
                    <Unauthorized />
                </UserLayout>
            } />
            {/* Subscription Success: this is the page users land on after Stripe calls our return URL post-payment */}
            <Route path="/subscription/success" element={
                <UserLayout>
                    <SubscriptionSuccess />
                </UserLayout>
            } />

            {/* **************  Admin ************** */}
            {createAuthRoute("/admin", <WelcomeMain />)}
            {createAuthRoute(
                "/admin/advertisement/app-screenshot-links",
                <AppAndScreenshotLinksPage />,
            )}
            {createAuthRoute(
                "/admin/advertisement/app-screenshot-links/all/:app",
                <ScreenshotGalleryAllPage />,
            )}
            {createAuthRoute(
                "/admin/advertisement/app-screenshot-links/google-ad/:app",
                <AppGoogleAdPage />,
            )}
            {createAuthRoute(
                "/admin/advertisement/app-screenshot-links/facebook-ad/:app",
                <AppFacebookAdPage />,
            )}
            {createAuthRoute(
                "/admin/advertisement/app-screenshot-links/amazon/:app",
                <AppAmazonTabletPage />,
            )}
            {createAuthRoute(
                "/admin/welcome/app-screenshot-links",
                <Navigate to="/admin/advertisement/app-screenshot-links" replace />,
            )}
            {createAuthRoute("/admin/account", <AdminAccountMain />)}
            {createAuthRoute("/admin/account/credit", <CreditMain />)}
            {createAuthRoute("/admin/account/coins", <PuzzleCoinsMain />)}
            {createAuthRoute("/admin/account/leader-board", <LeaderBoardMain />)}
            {createAuthRoute("/admin/account/speed-rank", <SpeedRankMain />)}
            {createAuthRoute("/admin/account/subscription/:userId", <UserDetails />)}
            {createAuthRoute(ADMIN_CRM_PATH, <RedirectToCrmHome />)}
            {createAuthRoute(ADMIN_CRM_MAILING_LIST_PATH, <MailingListMain />)}
            {createAuthRoute(ADMIN_CRM_CONTACTS_PATH, <ContactMain />)}
            {createAuthRoute(`${ADMIN_CRM_REFERRAL_PATH}/:eventId/inviters`, <ReferralInvitersPage />)}
            {createAuthRoute(`${ADMIN_CRM_REFERRAL_PATH}/:eventId`, <ReferralEventDetail />)}
            {createAuthRoute(ADMIN_CRM_REFERRAL_PATH, <ReferralMain />)}
            {createAuthRoute(`${ADMIN_CRM_FEEDBACK_PATH}/:feedbackId`, <AdminFeedbackDetail />)}
            {createAuthRoute(ADMIN_CRM_FEEDBACK_PATH, <AdminFeedbackMain />)}
            {createAuthRoute(ADMIN_CRM_REVIEW_PATH, <AdminAppReviewMain />)}
            {createAuthRoute("/admin/contacts", <RedirectLegacyContacts />)}
            {createAuthRoute("/admin/referral/:eventId/inviters", <RedirectLegacyReferralInviters />)}
            {createAuthRoute("/admin/referral/:eventId", <RedirectLegacyReferralEvent />)}
            {createAuthRoute("/admin/referral", <RedirectLegacyReferral />)}
            {createAuthRoute("/admin/mailing_list", <RedirectLegacyMailingList />)}
            {createAuthRoute("/admin/puzzle", <AdminPuzzleMain />)}
            {createAuthRoute("/admin/puzzle/grid", <AdminGridMain />)}
            {createAuthRoute("/admin/puzzle/grid/detail/:gridId", <AdminGridDetail />)}
            {createAuthRoute("/admin/puzzle/grid_tpl", <AdminGridTplMain />)}
            {createAuthRoute("/admin/puzzle/grid_pack", <AdminGridPackMain />)}
            {createAuthRoute("/admin/puzzle/grid_tpl/create", <GridCreateTemplate />)}
            {createAuthRoute("/admin/puzzle/grid_tpl/detail/:hwTplId", <AdminGridTplDetail />)}
            {createAuthRoute("/admin/puzzle/grid_tpl/verify-coord", <AdminGridTplVerifyCoord />)}
            {createAuthRoute("/admin/puzzle/grid_play", <AdminPlayMain />)}
            {createAuthRoute("/admin/puzzle/grid_stats", <AdminGridStatsMain />)}
            {createAuthRoute("/admin/puzzle/grid_board", <AdminGridBoardMain />)}
            {createAuthRoute("/admin/puzzle/grid_board/draft/:gridId/edit", <GridCandEdit />)}
            {createAuthRoute("/admin/puzzle/grid_board/draft/:id", <Step1Draft />)}
            {createAuthRoute("/admin/puzzle/grid_board/ready-to-review/:id", <Step2ReadyToReview />)}
            {createAuthRoute("/admin/puzzle/grid_board/reviewed/:id", <Step3Reviewed />)}
            {createAuthRoute("/admin/puzzle/grid_board/published/:id", <Step4Published />)}

            {/* Admin - Puzzle - Cross */}
            {createAuthRoute("/admin/puzzle/cross_board", <AdminCrossBoardMain />)}
            {createAuthRoute("/admin/puzzle/cross_board/draft/:crossId/edit", <CrossWordCandEdit />)}
            {createAuthRoute("/admin/puzzle/cross_board/draft/:id", <CrossStep1Draft />)}
            {createAuthRoute("/admin/puzzle/cross_board/ready-to-review/:id", <CrossStep2ReadyToReview />)}
            {createAuthRoute("/admin/puzzle/cross_board/reviewed/:id", <CrossStep3Reviewed />)}
            {createAuthRoute("/admin/puzzle/cross_board/published/:id", <CrossStep4Published />)}

            {/* Admin - Puzzle - Cross Template */}
            {createAuthRoute("/admin/puzzle/cross_tpl", <AdminCrossTplMain />)}
            {createAuthRoute("/admin/puzzle/cross_tpl/create", <CrossCreateTemplate />)}
            {createAuthRoute("/admin/puzzle/cross_tpl/detail/:crossTplId", <AdminCrossTplDetail />)}
            {createAuthRoute("/admin/puzzle/cross_tpl/verify-coord", <AdminCrossTplVerifyCoord />)}

            {/* Admin - Puzzle - Cross Play */}
            {createAuthRoute("/admin/puzzle/cross_play", <AdminCrossPlayMain />)}
            {createAuthRoute("/admin/puzzle/cross_stats", <AdminCrossStatsMain />)}

            {/* Admin - Puzzle - Word Wheel */}
            {createAuthRoute("/admin/puzzle/word_wheel_board", <AdminWordWheelBoardMain />)}
            {createAuthRoute("/admin/puzzle/word_wheel_board/quick-publish/:id", <WordWheelQuickPublisher />)}
            {createAuthRoute("/admin/puzzle/word_wheel_board/quick-publish", <WordWheelQuickPublisher />)}
            {createAuthRoute("/admin/puzzle/word_wheel_board/draft/:id", <WordWheelStep1Draft />)}
            {createAuthRoute("/admin/puzzle/word_wheel_board/ready-to-review/:id", <WordWheelStep2ReadyToReview />)}
            {createAuthRoute("/admin/puzzle/word_wheel_board/reviewed/:id", <WordWheelStep3Reviewed />)}
            {createAuthRoute("/admin/puzzle/word_wheel_board/published/:id", <WordWheelStep4Published />)}
            {createAuthRoute("/admin/puzzle/word_wheel_tpl", <AdminWordWheelTplMain />)}
            {createAuthRoute("/admin/puzzle/word_wheel_pack", <AdminWordWheelPackMain />)}
            {createAuthRoute("/admin/puzzle/word_wheel_journey", (
                <Navigate to="/admin/puzzle/word_wheel_pack?tab=journey" replace />
            ))}
            {createAuthRoute("/admin/puzzle/word_wheel_tpl/detail/:id", <AdminWordWheelTplDetail />)}
            {createAuthRoute("/admin/puzzle/word_wheel_play", <AdminWordWheelPlayMain />)}
            {createAuthRoute("/admin/puzzle/word_wheel_stats", <AdminWordWheelStatsMain />)}

            {/* Sliding — `browse` / `play` before `:storyId` so those paths are not parsed as UUIDs */}
            {createAuthRoute("/admin/sliding/browse", <AdminSlidingMain />)}
            {createAuthRoute("/admin/sliding/play", <AdminSlidingPlayMain />)}
            {createAuthRoute("/admin/sliding/play/:playId", <AdminSlidingPlayDetailPage />)}
            {createAuthRoute("/admin/sliding/stats", <AdminSlidingStatsMain />)}
            {createAuthRoute("/admin/sliding/packs", <AdminSlidingPackPage />)}
            {createAuthRoute("/admin/sliding/:storyId", <AdminSlidingStoryDetail />)}
            {createAuthRoute("/admin/sliding", <Navigate to={ADMIN_SLIDING_LANDING_PATH} replace />)}
            {createAuthRoute("/admin/story/sliding", <Navigate to="/admin/sliding/browse" replace />)}

            {/* Jigsaw */}
            {createAuthRoute("/admin/jigsaw/browse", <AdminJigsawMain />)}
            {createAuthRoute(
                "/admin/jigsaw/browse/:storyId/color-baskets/:jigsawId",
                <AdminJigsawColorBasketDetailPage />,
            )}
            {createAuthRoute("/admin/jigsaw/browse/:storyId", <AdminJigsawDetailPage />)}
            {createAuthRoute("/admin/jigsaw/template", <AdminJigsawTemplateMain />)}
            {createAuthRoute("/admin/jigsaw/packs/:packId", <AdminJigsawPackDetailPage />)}
            {createAuthRoute("/admin/jigsaw/packs", <AdminJigsawPackPage />)}
            {createAuthRoute("/admin/jigsaw/template/create", <CreateJigsawTemplatePage />)}
            {createAuthRoute("/admin/jigsaw/template/edit/:id", <EditJigsawTemplatePage />)}
            {createAuthRoute("/admin/jigsaw/play", <AdminJigsawPlayMain />)}
            {createAuthRoute("/admin/jigsaw/play/:playId", <AdminJigsawPlayDetailPage />)}
            {createAuthRoute("/admin/jigsaw/stats", <AdminJigsawStatsMain />)}
            {createAuthRoute("/admin/jigsaw", <Navigate to="/admin/jigsaw/browse" replace />)}
            {createAuthRoute("/admin/story", <AdminStoryMain />)}
            {createAuthRoute("/admin/story/:storyId", <AdminStoryDetail />)}

            {/* Feedback */}
            {createAuthRoute("/admin/feedback/:feedbackId", <RedirectLegacyFeedbackDetail />)}
            {createAuthRoute("/admin/feedback", <RedirectLegacyFeedback />)}
            {createAuthRoute("/admin/notification", <AdminNotificationMain />)}

            {/* Setting */}
            {createAuthRoute("/admin/setting", <SettingMain />)}
            {createAuthRoute("/admin/tutor", <AdminTutorBoardPage />)}
            {createAuthRoute("/admin/tutor/board", <AdminTutorBoardPage />)}
            {createAuthRoute("/admin/advertisement", <AdvertisementMain />)}
            {createAuthRoute("/admin/advertisement/tutors", <TutorsAdPage />)}
            {createAuthRoute("/admin/advertisement/product-icon", <ProductIconsPage />)}
            {createAuthRoute("/admin/advertisement/promotion/new", <PromotionEditPage />)}
            {createAuthRoute("/admin/advertisement/promotion/:code", <PromotionEditPage />)}
            {createAuthRoute("/admin/advertisement/promotion", <PromotionMain />)}
            {createAuthRoute(
                "/admin/advertisement/weekly/template",
                <Navigate to="/admin/advertisement/promotion" replace state={{ weeklyTab: "template" }} />,
            )}
            {createAuthRoute("/admin/advertisement/weekly/:id", <WeeklyIssuePage />)}
            {createAuthRoute(
                "/admin/advertisement/stripe-image",
                <Navigate to="/admin/advertisement/product-icon" replace />,
            )}
            {createAuthRoute("/admin/advertisement/iphone-background", <IphoneBackgroundFromStoryMain />)}
            {createAuthRoute("/admin/advertisement/pc-background", <PcBackgroundFromStoryMain />)}
            {createAuthRoute("/admin/advertisement/resources/:id", <AdResourceDetailPage />)}
            {createAuthRoute("/admin/advertisement/generate", <GenerateAdImageMain />)}
            {createAuthRoute(
                "/admin/advertisement/social",
                <Navigate to="/admin/advertisement/social/facebook" replace />,
            )}
            {createAuthRoute("/admin/advertisement/social/facebook", <TestFacebookMain />)}
            {createAuthRoute("/admin/advertisement/social/instagram", <TestInstagramMain />)}
            {createAuthRoute("/admin/advertisement/social/word-wheel", <WordWheelGuessSocialMain />)}
            {createAuthRoute(
                "/admin/advertisement/social/word-wheel-grid",
                <WordWheelGuessSocialMain layout="grid" />,
            )}
            {createAuthRoute(
                "/admin/advertisement/app-icon",
                <Navigate to="/admin/advertisement/generate?resourceType=JIGSAW_APP_ICON" replace />,
            )}
            {createAuthRoute(
                "/admin/advertisement/feature-graphic",
                <Navigate
                    to="/admin/advertisement/generate?resourceType=SLIDING_FEATURE_GRAPHIC"
                    replace
                />,
            )}
            {createAuthRoute(
                "/admin/advertisement/sliding-feature-graphic",
                <Navigate
                    to="/admin/advertisement/generate?resourceType=SLIDING_FEATURE_GRAPHIC"
                    replace
                />,
            )}
            {createAuthRoute(
                "/admin/system/ad/sliding-feature-graphic",
                <Navigate
                    to="/admin/advertisement/generate?resourceType=SLIDING_FEATURE_GRAPHIC"
                    replace
                />,
            )}
            {createAuthRoute("/admin/system/ad", <Navigate to="/admin/advertisement" replace />)}
            {/* Setting - Topic */}
            {createAuthRoute("/admin/topic", <AdminTopicMain />)}
            {createAuthRoute("/admin/topic/create", <CreateTopic />)}
            {createAuthRoute("/admin/topic/detail/:topicId", <TopicDetail />)}
            {/* Setting - Genre */}
            {createAuthRoute("/admin/genre", <GenreMain />)}
            {createAuthRoute("/admin/banned", <BannedMain />)}
            {createAuthRoute("/admin/bedrock-idea", <BedrockIdeaMain />)}
            {createAuthRoute("/admin/bedrock-idea/new", <BedrockIdeaFormPage />)}
            {createAuthRoute("/admin/bedrock-idea/edit/:id", <BedrockIdeaFormPage />)}
            {createAuthRoute("/admin/publisher", <PublisherScheduleMain />)}
            {createAuthRoute("/admin/publisher/new", <PublisherScheduleFormPage />)}
            {createAuthRoute("/admin/publisher/edit/:id", <PublisherScheduleFormPage />)}
            {createAuthRoute("/admin/task-scheduler", <TaskSchedulerMain />)}
            {createAuthRoute("/admin/task-scheduler/new", <TaskSchedulerFormPage />)}
            {createAuthRoute("/admin/task-scheduler/edit/:id", <TaskSchedulerFormPage />)}
            {createAuthRoute("/admin/audio", <AdminAudioMain />)}
            {createAuthRoute("/admin/audio/new", <AdminAudioFormPage />)}
            {createAuthRoute("/admin/audio/edit/:id", <AdminAudioFormPage />)}
            {createAuthRoute("/admin/logs", <SysLogsMain />)}
            {createAuthRoute("/admin/statistics", <AdminStatisticsMain />)}
            {/* Setting - System */}
            {createAuthRoute("/admin/system", <SystemMain />)}
            {createAuthRoute("/admin/system/tag-migration", <TagMigrationMain />)}

            {createAuthRoute("/admin/system/prototype", <PrototypeMain />)}
            {createAuthRoute("/admin/system/integrate", <IntegrateMain />)}
            {createAuthRoute("/admin/system/integrate/dictionary", <DictionaryApiMain />)}
            {createAuthRoute("/admin/system/integrate/cambridge", <CambridgeMain />)}
            {createAuthRoute("/admin/system/integrate/datamuse", <DatamuseApiMain />)}
            {createAuthRoute("/admin/system/integrate/word-network", <WordNetworkMain />)}
            {createAuthRoute("/admin/system/integrate/guardian", <GuardianMain />)}
            {createAuthRoute("/admin/system/ingest", <IngestMain />)}
            {createAuthRoute("/admin/system/integrate/openlibrary/detail/*", <OpenLibraryDetail />)}
            {createAuthRoute("/admin/system/integrate/openlibrary", <OpenLibraryMain />)}
            {createAuthRoute("/admin/system/integrate/puzzle-interact-api", <PiDicApiMain />)}

            {/* Setting - System anything*/}
            {createAuthRoute("/admin/system/anything", <AnythingMain />)}
            {createAuthRoute("/admin/system/anything/crypto", <CryptoMain />)}
            {createAuthRoute("/admin/system/anything/genre", <GenreMain />)}
            {createAuthRoute("/admin/system/anything/grid", <BoilerplateDataGrid />)}
            {createAuthRoute("/admin/system/anything/credentials", <CredentialsMain />)}
            {createAuthRoute("/admin/system/anything/file-upload", <FileUploadMain />)}
            {createAuthRoute("/admin/system/anything/email", <EmailMain />)}
            {createAuthRoute("/admin/system/anything/gen-image-preview", <GenImagePreviewMain />)}
            {createAuthRoute("/admin/system/anything/translate", <TranslateMain />)}
            {createAuthRoute("/admin/system/anything/hook-it", <HookItMain />)}
            {createAuthRoute("/admin/system/anything/create-french-puzzles", <CreateFrenchPuzzlesMain />)}
            {createAuthRoute("/admin/system/anything/wordsearch-place-words", <WordSearchPlaceWordsMain />)}
            {createAuthRoute("/admin/system/anything/dic-play-word", <DicPlayWordMain />)}
            {createAuthRoute("/admin/system/anything/word-of-day-batch", <WordOfDayBatchMain />)}
            {createAuthRoute(
                "/admin/system/anything/test-instagram",
                <Navigate to="/admin/advertisement/social/instagram" replace />,
            )}
            {createAuthRoute(
                "/admin/system/anything/test-facebook",
                <Navigate to="/admin/advertisement/social/facebook" replace />,
            )}
            {createAuthRoute("/admin/system/anything/test-jigsaw", <TestJigsawMain />)}
            {createAuthRoute("/admin/system/anything/test-jigsaw-canvas", <TestJigsawCanvasPlayPage />)}
            {createAuthRoute("/admin/system/anything/svg-to-json", <SvgToJsonPage />)}
            {createAuthRoute("/admin/system/anything/svg-overlay", <SvgOverlayConverterPage />)}
            {createAuthRoute("/admin/system/anything/test-prompt-v2", <TestPromptV2Main />)}
            {createAuthRoute("/admin/system/anything/test-word-puzzle-photo", <TestWordPuzzlePhotoMain />)}
            {createAuthRoute("/admin/system/anything/test-adsense", <AdSenseTestMain />)}
            {createAuthRoute("/admin/system/anything/test-monetag", <MonetagTestMain />)}
            {createAuthRoute("/admin/system/anything/send-notification", <SendNotificationMain />)}
            {createAuthRoute(
                "/admin/system/anything/post-word-wheel",
                <Navigate to="/admin/advertisement/social/word-wheel" replace />,
            )}
            {createAuthRoute("/admin/system/stripe", <StripeMain />)}

            {/* Catch-all route for 404 - must be last */}
            <Route
                path="*"
                element={
                    <PublicChromeLessPage>
                        <NotFound />
                    </PublicChromeLessPage>
                }
            />
            </Routes>
        </>
    );
}

export default App;
